/* eslint-disable react/no-array-index-key */
import { zodResolver } from '@hookform/resolvers/zod';
import { blue, green } from '@mui/material/colors';
import Atestado from 'pages/atestado/Atestado';
import FormReceitas from 'pages/receita/FormReceitas';
import Requisicao from 'pages/requisicao/Requisicao';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectFunc } from 'redux/features/auth/authSlice';
// import api from 'services/api'; // list-ia / upload-ia desativados
import { selectProntuariosByPac, updateProntuario } from 'services/prontuario';
import useSWR from 'swr';
import { isNumericId } from 'utils/numericId';
import { z } from 'zod';
import {
  antecedentes,
  conduta,
  fisico,
  habitos,
  hipotese,
  queixas,
} from '../../../utils/prontuario';
import ArraySelector from './ArraySelector';
import CampoClinico from './CampoClinico';
import DadosPaciente from './DadosPaciente';
import { generatePdfProntuario } from './generatePdfProntuario';
import Sidebar from './Sidebar';

const ABAS_PRONTUARIO = ['prontuario', 'atestado', 'receita', 'requisicao'];

// Anexo de documento (list-ia / upload-ia) desativado — não usado hoje.
// const PATIENT_DOC_PROCEDIMENTO = 'laboratorio';
// const PATIENT_DOC_PREFIX = 'DOCPAC_';
// const LAB_DOC_PREFIX = 'LAB_'; // PDF de exame laboratorial / IA desativados

// function isPatientDocFilename(filename = '') {
//   const normalized = String(filename).trim().toUpperCase();
//   if (!normalized) return false;
//   const withoutTimestamp = normalized.replace(/^\d+-/, '');
//   return withoutTimestamp.startsWith(PATIENT_DOC_PREFIX);
// }

// function getPatientDocScopeId(prontuario) {
//   return String(prontuario?.CODPAC || '').trim();
// }

// function getLegacyPatientDocScopeId(prontuario) {
//   return String(prontuario?.CODCONSULTA || '').trim();
// }

const prontuarioSchema = z.object({
  QUEIXAS: z.string().optional(),
  MEDICAMENTOS: z.string().optional(),
  HABITOS: z.string().optional(),
  ISDA: z.string().optional(),
  ANTECEDENTES_PESSOAIS: z.string().optional(),
  ANTECEDENTES_FLIA: z.string().optional(),
  EXAMES_COMPLEMENTARES: z.string().optional(),
  EXAME_FISICO: z.string().optional(),
  HIPOTESE_DIAGNOSTICA: z.string().optional(),
  CONDUTA: z.string().optional(),
  OBS: z.string().optional(),
  CID: z.string().optional(),
  CID2: z.string().optional(),
  CID3: z.string().optional(),
});

export default function ProntuarioV2() {
  const location = useLocation();
  const { agendaSelecionada } = location.state || {};
  const { codpac } = useParams();
  const hasValidCodpac = isNumericId(codpac);
  // const doctor = useSelector(selectDoctor); // só gateava o anexo de PDF laboratorial
  const func = useSelector(selectFunc);
  const [codConsultaSelecionada, setCodConsultaSelecionada] = useState(null);
  const [gravadoLocalSim, setGravadoLocalSim] = useState(false);
  // PDF de exame laboratorial e análise por IA desativados — não usados hoje.
  // const [uploadingLabPdf, setUploadingLabPdf] = useState(false);
  // const [analyzingLabPdf, setAnalyzingLabPdf] = useState(false);
  // const [viewingLabPdf, setViewingLabPdf] = useState(false);
  // const [showLabDocModal, setShowLabDocModal] = useState(false);
  // const [loadingLabDocs, setLoadingLabDocs] = useState(false);
  // const [labDocFiles, setLabDocFiles] = useState([]);
  // const [deletingLabDocKey, setDeletingLabDocKey] = useState('');
  // const [lastLabPdfKey, setLastLabPdfKey] = useState('');
  // const [uploadingPatientDoc, setUploadingPatientDoc] = useState(false);
  // const [viewingPatientDoc, setViewingPatientDoc] = useState(false);
  // const [showPatientDocModal, setShowPatientDocModal] = useState(false);
  // const [loadingPatientDocs, setLoadingPatientDocs] = useState(false);
  // const [patientDocFiles, setPatientDocFiles] = useState([]);
  // const [deletingPatientDocKey, setDeletingPatientDocKey] = useState('');
  const [loadingAtividades, setLoadingAtividades] = useState(false);
  const [dadosParaConfirmarCopia, setDadosParaConfirmarCopia] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState(() =>
    ABAS_PRONTUARIO.includes(location.state?.aba) ? location.state.aba : 'prontuario'
  );

  useEffect(() => {
    const aba = location.state?.aba;
    if (ABAS_PRONTUARIO.includes(aba)) {
      setAbaAtiva(aba);
    }
  }, [location.state?.aba]);
  // const labPdfInputRef = useRef(null);
  // const patientDocInputRef = useRef(null);
  const isFuncionario = !!func;

  const {
    data: prontuarios,
    isLoading,
    error,
    mutate,
  } = useSWR(hasValidCodpac ? ['prontuarios', codpac] : null, () => selectProntuariosByPac(codpac));

  const prontuarioDaLista = useMemo(() => {
    if (!prontuarios?.length) return null;
    if (codConsultaSelecionada == null) return prontuarios[0];
    return (
      prontuarios.find((item) => item.CODCONSULTA === codConsultaSelecionada) || prontuarios[0]
    );
  }, [prontuarios, codConsultaSelecionada]);

  const prontuario =
    gravadoLocalSim && prontuarioDaLista
      ? { ...prontuarioDaLista, GRAVADO: 'SIM' }
      : prontuarioDaLista;

  const extrairValoresFormulario = useCallback(
    (p) => ({
      QUEIXAS: p?.QUEIXAS || '',
      MEDICAMENTOS: p?.MEDICAMENTOS || '',
      HABITOS: p?.HABITOS || '',
      ANTECEDENTES_PESSOAIS: p?.ANTECEDENTES_PESSOAIS || '',
      ANTECEDENTES_FLIA: p?.ANTECEDENTES_FLIA || '',
      EXAMES_COMPLEMENTARES: p?.EXAMES_COMPLEMENTARES || '',
      EXAME_FISICO: p?.EXAME_FISICO || '',
      ISDA: p?.ISDA || '',
      HIPOTESE_DIAGNOSTICA: p?.HIPOTESE_DIAGNOSTICA || '',
      CONDUTA: p?.CONDUTA || '',
      OBS: p?.OBS || '',
      CID: p?.CID || '',
      CID2: p?.CID2 || '',
      CID3: p?.CID3 || '',
    }),
    []
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(prontuarioSchema),
    defaultValues: {
      QUEIXAS: '',
      MEDICAMENTOS: '',
      HABITOS: '',
      ISDA: '',
      ANTECEDENTES_PESSOAIS: '',
      ANTECEDENTES_FLIA: '',
      EXAMES_COMPLEMENTARES: '',
      EXAME_FISICO: '',
      HIPOTESE_DIAGNOSTICA: '',
      CONDUTA: '',
      OBS: '',
      CID: '',
      CID2: '',
      CID3: '',
    },
  });

  const setProntuario = useCallback(
    (next) => {
      const resolved = typeof next === 'function' ? next(prontuario) : next;
      setCodConsultaSelecionada(resolved?.CODCONSULTA ?? null);
      setGravadoLocalSim(String(resolved?.GRAVADO || '').trim() === 'SIM');
      if (resolved) {
        reset(extrairValoresFormulario(resolved));
      }
    },
    [prontuario, reset, extrairValoresFormulario]
  );

  const isReadOnly = prontuario?.GRAVADO?.trim() === 'SIM';

  useEffect(() => {
    if (prontuario) {
      reset(extrairValoresFormulario(prontuario));
    }
  }, [prontuario?.CODCONSULTA, reset, extrairValoresFormulario]);

  // Função que executa a gravação após a confirmação no banner
  const handleConfirmarCopia = async () => {
    if (!dadosParaConfirmarCopia) return;

    try {
      const { consultaHoje, prontuarioAtualizado } = dadosParaConfirmarCopia;

      await updateProntuario(consultaHoje.CODCONSULTA, prontuarioAtualizado);
      await mutate();
      setProntuario(prontuarioAtualizado);

      toast.success('Dados copiados para a consulta atual com sucesso!');
    } catch (_err) {
      toast.error('Erro ao copiar dados da consulta.');
    } finally {
      setDadosParaConfirmarCopia(null);
    }
  };

  // Função centralizada para gravar parcialmente
  const handleGravarParcial = useCallback(async () => {
    if (!isReadOnly && prontuario?.CODCONSULTA) {
      try {
        const values = getValues();
        await updateProntuario(prontuario.CODCONSULTA, values);
        await mutate();
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Erro ao gravar parcialmente.');
      }
    }
  }, [isReadOnly, prontuario?.CODCONSULTA, getValues, mutate]);

  // Ref sempre atualizada para salvar no unmount sem travar no estado do React
  const latestSaveRef = useRef({
    handleGravarParcial,
    isReadOnly,
    codConsulta: prontuario?.CODCONSULTA,
  });

  useEffect(() => {
    latestSaveRef.current = {
      handleGravarParcial,
      isReadOnly,
      codConsulta: prontuario?.CODCONSULTA,
    };
  }, [handleGravarParcial, isReadOnly, prontuario?.CODCONSULTA]);

  // Garante salvar qualquer alteração pendente no unmount (se fechar a tela ou mudar de rota)
  useEffect(() => {
    return () => {
      const {
        handleGravarParcial: save,
        isReadOnly: readOnly,
        codConsulta,
      } = latestSaveRef.current;
      if (!readOnly && codConsulta) {
        save();
      }
    };
  }, []);

  const appendSelectorItem = (fieldName, item) => {
    const prev = String(getValues(fieldName) || '');
    const trimmed = prev.trim();
    const lines = trimmed ? trimmed.split('\n') : [];

    if (lines[lines.length - 1] === item) {
      return;
    }

    const next = trimmed ? `${trimmed}\n${item}` : item;
    setValue(fieldName, next, { shouldDirty: true, shouldTouch: true });
    handleGravarParcial();
  };

  const onSubmit = async (data) => {
    try {
      const resposta = await updateProntuario(prontuario?.CODCONSULTA, data);
      await mutate();
      toast.success('Prontuário atualizado com sucesso!');
      return resposta;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao atualizar prontuário');
      return null;
    }
  };

  // Garante gravação instantânea antes de mudar para a tela de ECG / Atividades
  // Função atualizada para abrir em nova aba
  const abrirAtividades = async (ecg = false) => {
    try {
      setLoadingAtividades(true);

      // 1. Salva os dados do prontuário primeiro para não perder nada
      await handleGravarParcial();

      const params = new URLSearchParams({
        codpac: prontuario?.CODPAC ?? '',
        ...(prontuario?.NOMEPAC ? { nomePaciente: prontuario.NOMEPAC } : {}),
        ...(ecg ? { ecg: '1' } : {}),
      });
      const url = `/atividades?${params.toString()}`;

      // 3. Abre em uma nova aba do navegador
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error('Erro ao salvar os dados antes de abrir em nova aba.');
    } finally {
      setLoadingAtividades(false);
    }
  };

  const handleImprimirProntuario = async ({ prontuario: p }) => {
    try {
      const blob = await generatePdfProntuario({ prontuario: p });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Prontuario-${p?.CODCONSULTA}-${p?.NOMEPAC}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF!');
    }
  };

  /* PDF de exame laboratorial e análise por IA desativados — não usados hoje.
  const handlePickLabPdf = () => {
    labPdfInputRef.current?.click();
  };

  // TODO; isso é usado???
  const handleAttachLabPdf = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !prontuario?.CODCONSULTA) return;

    if (file.type !== 'application/pdf') {
      toast.warn('Selecione um arquivo PDF válido.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.warn('Arquivo muito grande. Máximo 20MB.');
      return;
    }

    try {
      setUploadingLabPdf(true);
      const normalizedFileName = isPatientDocFilename(file.name)
        ? file.name.replace(/^\d+-/, '').replace(PATIENT_DOC_PREFIX, LAB_DOC_PREFIX)
        : file.name.startsWith(LAB_DOC_PREFIX)
          ? file.name
          : `${LAB_DOC_PREFIX}${file.name}`;
      const renamedFile = new File([file], normalizedFileName, {
        type: file.type,
      });

      const formData = new FormData();
      formData.append('file', renamedFile);
      formData.append('codLaudo', String(prontuario.CODCONSULTA));
      formData.append('procedimento', 'laboratorio');

      const { data: uploadData } = await api.post('/upload-ia', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (uploadData?.key) {
        setLastLabPdfKey(uploadData.key);
      }

      toast.success('PDF laboratorial anexado com sucesso.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao anexar PDF laboratorial.');
    } finally {
      setUploadingLabPdf(false);
    }
  };

  const getLatestLabPdfKey = async () => {
    const { data } = await api.get('list-ia', {
      params: {
        codLaudo: prontuario.CODCONSULTA,
        procedimento: 'laboratorio',
      },
    });

    const arquivosPdf = (Array.isArray(data) ? data : []).filter((item) => {
      const key = item?.Key || '';
      const filename = key.split('/').pop() || '';
      return key.toLowerCase().endsWith('.pdf') && !isPatientDocFilename(filename);
    });

    if (arquivosPdf.length === 0) return '';

    const ordenados = [...arquivosPdf].sort(
      (a, b) => new Date(b?.LastModified || 0).getTime() - new Date(a?.LastModified || 0).getTime()
    );

    return ordenados[0]?.Key || '';
  };

  const getLabDocCandidates = async () => {
    const consultationScopeIds = Array.from(
      new Set(
        (Array.isArray(prontuarios) ? prontuarios : [])
          .map((p) => String(p?.CODCONSULTA || '').trim())
          .filter(Boolean)
      )
    );

    if (consultationScopeIds.length === 0 && prontuario?.CODCONSULTA) {
      consultationScopeIds.push(String(prontuario.CODCONSULTA));
    }

    if (consultationScopeIds.length === 0) return [];

    const allFiles = [];

    for (const scopeId of consultationScopeIds) {
      try {
        const { data } = await api.get('list-ia', {
          params: {
            codLaudo: scopeId,
            procedimento: 'laboratorio',
          },
        });

        const arquivosPdf = (Array.isArray(data) ? data : []).filter((item) => {
          const key = item?.Key || '';
          const filename = key.split('/').pop() || '';
          return key.toLowerCase().endsWith('.pdf') && !isPatientDocFilename(filename);
        });

        arquivosPdf.forEach((item) => {
          allFiles.push({ ...item, scopeId });
        });
      } catch (_err) {
        // Ignora falhas pontuais
      }
    }

    if (allFiles.length === 0) return [];

    const ordenados = [...allFiles].sort(
      (a, b) => new Date(b?.LastModified || 0).getTime() - new Date(a?.LastModified || 0).getTime()
    );

    const unicos = [];
    const vistos = new Set();
    for (const item of ordenados) {
      const key = item?.Key || '';
      if (!key || vistos.has(key)) continue;
      vistos.add(key);
      unicos.push({
        key,
        scopeId: item.scopeId,
        lastModified: item?.LastModified,
      });
    }

    return unicos;
  };

  const loadLabDocFiles = async () => {
    setLoadingLabDocs(true);
    try {
      const keys = await getLabDocCandidates();
      const files = keys.map((key) => {
        const filename = key.key.split('/').pop() || key.key;
        const tsMatch = filename.match(/^(\d+)-/);
        const createdAt = tsMatch
          ? new Date(Number(tsMatch[1]))
          : key.lastModified
            ? new Date(key.lastModified)
            : null;
        return {
          key: key.key,
          scopeId: key.scopeId,
          filename,
          createdAt,
        };
      });
      setLabDocFiles(files);
    } catch (err) {
      setLabDocFiles([]);
      toast.error(err?.response?.data?.message || 'Erro ao listar PDFs laboratoriais.');
    } finally {
      setLoadingLabDocs(false);
    }
  };

  const openLabDocByKey = async (key, scopeId) => {
    const openScopeId = String(scopeId || prontuario?.CODCONSULTA || '').trim();
    if (!key || !openScopeId) return false;

    const filename = key.split('/').pop();
    const keyProcedimento = key.split('/')[0] || 'laboratorio';
    const procedimentosTentativa = [keyProcedimento, 'laboratorio'];

    for (const procedimento of procedimentosTentativa) {
      try {
        const { data } = await api.get('/presign-view-ia', {
          params: {
            codLaudo: openScopeId,
            procedimento,
            filename,
          },
        });

        if (data?.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          return true;
        }
      } catch (_err) {
        // Tenta próxima opção
      }
    }

    return false;
  };

  const handleViewLabPdf = async () => {
    setShowLabDocModal(true);
    await loadLabDocFiles();
  };

  const handleOpenLabDocFromModal = async (file) => {
    try {
      setViewingLabPdf(true);
      const opened = await openLabDocByKey(file?.key, file?.scopeId);
      if (!opened) {
        toast.warn('Não foi possível abrir este PDF laboratorial.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao abrir PDF laboratorial.');
    } finally {
      setViewingLabPdf(false);
    }
  };

  const handleDeleteLabDocFromModal = async (file) => {
    if (!file?.key) return;

    const confirmed = window.confirm(`Deseja excluir o PDF laboratorial ${file.filename}?`);
    if (!confirmed) return;

    try {
      setDeletingLabDocKey(file.key);
      await api.delete('/delete-ia', {
        data: {
          key: file.key,
        },
      });
      toast.success('PDF laboratorial excluído com sucesso.');
      await loadLabDocFiles();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao excluir PDF laboratorial.');
    } finally {
      setDeletingLabDocKey('');
    }
  };

  const handleProcessLabPdf = async () => {
    if (!prontuario?.CODCONSULTA) return;

    try {
      setAnalyzingLabPdf(true);

      let key = lastLabPdfKey;
      if (!key) {
        key = await getLatestLabPdfKey();
      }

      if (!key) {
        toast.warn('Nenhum PDF laboratorial anexado para esta consulta.');
        return;
      }

      const { data: analysisData } = await api.post('/analyze-laboratorio-ia', {
        codConsulta: prontuario.CODCONSULTA,
        key,
      });

      const historicoLaboratorial =
        analysisData?.ANTECEDENTES_FLIA ||
        analysisData?.historicoTexto ||
        analysisData?.texto ||
        analysisData?.history ||
        '';

      if (!historicoLaboratorial) {
        toast.warn('A IA não retornou conteúdo para exames laboratoriais.');
        return;
      }

      setValue('ANTECEDENTES_FLIA', historicoLaboratorial, {
        shouldDirty: true,
        shouldTouch: true,
      });

      await updateProntuario(prontuario.CODCONSULTA, {
        ...getValues(),
        ANTECEDENTES_FLIA: historicoLaboratorial,
      });

      await mutate();
      setLastLabPdfKey(key);
      toast.success('Exames laboratoriais importados para ANTECEDENTES_FLIA.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao processar PDF laboratorial.');
    } finally {
      setAnalyzingLabPdf(false);
    }
  };

  */

  /* Anexo de documento (list-ia / upload-ia) desativado — não usado hoje.
  const handlePickPatientDoc = () => {
    patientDocInputRef.current?.click();
  };

  const handleAttachPatientDoc = async (event) => {
    const file = event.target.files?.[0];
    const patientScopeId = getPatientDocScopeId(prontuario);

    event.target.value = '';

    if (!file || !patientScopeId) return;

    if (file.type !== 'application/pdf') {
      toast.warn('Selecione um arquivo PDF válido.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.warn('Arquivo muito grande. Máximo 20MB.');
      return;
    }

    try {
      setUploadingPatientDoc(true);
      const normalizedFileName = file.name.startsWith(PATIENT_DOC_PREFIX)
        ? file.name
        : `${PATIENT_DOC_PREFIX}${file.name}`;
      const renamedFile = new File([file], normalizedFileName, {
        type: file.type,
      });

      const formData = new FormData();
      formData.append('file', renamedFile);
      formData.append('codLaudo', patientScopeId);
      formData.append('procedimento', PATIENT_DOC_PROCEDIMENTO);

      await api.post('/upload-ia', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Documento do paciente anexado com sucesso.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao anexar documento do paciente.');
    } finally {
      setUploadingPatientDoc(false);
    }
  };

  const getPatientDocCandidates = async () => {
    const patientScopeId = getPatientDocScopeId(prontuario);
    const legacyScopeId = getLegacyPatientDocScopeId(prontuario);
    const scopeIds = [patientScopeId, legacyScopeId].filter(Boolean);

    if (scopeIds.length === 0) return [];

    const allFiles = [];

    for (const scopeId of scopeIds) {
      if (allFiles.some((f) => f.scopeId === scopeId)) continue;

      const { data } = await api.get('list-ia', {
        params: {
          codLaudo: scopeId,
          procedimento: PATIENT_DOC_PROCEDIMENTO,
        },
      });

      const arquivosPdf = (Array.isArray(data) ? data : []).filter((item) => {
        const key = item?.Key || '';
        const filename = key.split('/').pop() || '';
        return key.toLowerCase().endsWith('.pdf') && isPatientDocFilename(filename);
      });

      arquivosPdf.forEach((item) => {
        allFiles.push({ ...item, scopeId });
      });
    }

    if (allFiles.length === 0) return [];

    const ordenados = [...allFiles].sort(
      (a, b) => new Date(b?.LastModified || 0).getTime() - new Date(a?.LastModified || 0).getTime()
    );

    const unicos = [];
    const vistos = new Set();
    for (const item of ordenados) {
      const key = item?.Key || '';
      if (!key || vistos.has(key)) continue;
      vistos.add(key);
      unicos.push({
        key,
        scopeId: item.scopeId,
        lastModified: item?.LastModified,
      });
    }

    return unicos;
  };

  const loadPatientDocFiles = async () => {
    setLoadingPatientDocs(true);
    try {
      const keys = await getPatientDocCandidates();
      const files = keys.map((key) => {
        const filename = key.key.split('/').pop() || key.key;
        const tsMatch = filename.match(/^(\d+)-/);
        const createdAt = tsMatch
          ? new Date(Number(tsMatch[1]))
          : key.lastModified
            ? new Date(key.lastModified)
            : null;
        return {
          key: key.key,
          scopeId: key.scopeId,
          filename,
          createdAt,
        };
      });
      setPatientDocFiles(files);
    } catch (err) {
      setPatientDocFiles([]);
      toast.error(err?.response?.data?.message || 'Erro ao listar anexos do paciente.');
    } finally {
      setLoadingPatientDocs(false);
    }
  };

  const openPatientDocByKey = async (key, scopeId) => {
    const openScopeId = String(scopeId || getPatientDocScopeId(prontuario)).trim();
    if (!key || !openScopeId) return false;

    const filename = key.split('/').pop();
    const keyProcedimento = key.split('/')[0] || PATIENT_DOC_PROCEDIMENTO;
    const procedimentosTentativa = [
      keyProcedimento,
      PATIENT_DOC_PROCEDIMENTO,
      'documento_paciente',
      'documento',
    ];

    for (const procedimento of procedimentosTentativa) {
      try {
        const { data } = await api.get('/presign-view-ia', {
          params: {
            codLaudo: openScopeId,
            procedimento,
            filename,
          },
        });

        if (data?.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          return true;
        }
      } catch (_err) {
        // Tenta próxima opção
      }
    }

    return false;
  };

  const handleViewPatientDoc = async () => {
    if (!prontuario?.CODCONSULTA) return;

    setShowPatientDocModal(true);
    await loadPatientDocFiles();
  };

  const handleOpenPatientDocFromModal = async (file) => {
    try {
      setViewingPatientDoc(true);
      const opened = await openPatientDocByKey(file?.key, file?.scopeId);
      if (!opened) {
        toast.warn('Não foi possível abrir este anexo.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao abrir documento do paciente.');
    } finally {
      setViewingPatientDoc(false);
    }
  };

  // const handleDeletePatientDocFromModal = async (file) => {
  //   if (!file?.key) return;

  //   const confirmed = window.confirm(`Deseja excluir o anexo ${file.filename}?`);
  //   if (!confirmed) return;

  //   try {
  //     setDeletingPatientDocKey(file.key);
  //     await api.delete('/delete-ia', {
  //       data: {
  //         key: file.key,
  //       },
  //     });
  //     toast.success('Anexo excluído com sucesso.');
  //     await loadPatientDocFiles();
  //   } catch (err) {
  //     toast.error(err?.response?.data?.message || 'Erro ao excluir anexo.');
  //   } finally {
  //     setDeletingPatientDocKey('');
  //   }
  // };
  */

  return (
    <Card className="mt-2 mb-2">
      <Card.Header className="text-center font-weight-bold mb-0 h3">
        PRONTUÁRIO ELETRÔNICO
      </Card.Header>
      <Card.Body>
        {!hasValidCodpac && <div className="alert alert-warning">Código de paciente inválido.</div>}

        {hasValidCodpac && isLoading && (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        )}

        {hasValidCodpac && error && (
          <div className="alert alert-danger">
            <strong>Erro:</strong> {error.message}
          </div>
        )}

        {hasValidCodpac && !isLoading && !error && !prontuario && (
          <div className="alert alert-warning">Nenhum prontuário encontrado.</div>
        )}

        {!isLoading && !error && prontuario && (
          <div className="row align-items-start">
            <div className="col-md-2 pr-1">
              <Sidebar
                prontuarios={prontuarios}
                setProntuarios={mutate}
                prontuario={prontuario}
                setProntuario={setProntuario}
                isDisabled={isFuncionario}
                onSolicitarCopia={(payload) => setDadosParaConfirmarCopia(payload)}
              />
            </div>
            <div className="col-md-10">
              <DadosPaciente laudo={prontuario} agenda={agendaSelecionada} />
              <hr />
              {dadosParaConfirmarCopia && (
                <Alert variant="warning" className="my-3 shadow-sm border-warning">
                  <Alert.Heading className="fs-6 fw-bold mb-2">
                    ⚠️ Confirmação de Cópia de Prontuário
                  </Alert.Heading>
                  <p className="mb-3 text-dark">
                    Deseja substituir os campos clínicos da <strong>Consulta Atual</strong> pelos
                    dados da consulta de <strong>{dadosParaConfirmarCopia.dataOrigem}</strong>?
                  </p>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setDadosParaConfirmarCopia(null)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="fw-bold"
                      onClick={handleConfirmarCopia}
                    >
                      Confirmar e Copiar
                    </Button>
                  </div>
                </Alert>
              )}
              <Tab.Container
                id="prontuario-abas"
                activeKey={abaAtiva}
                onSelect={(key) => {
                  if (key) setAbaAtiva(key);
                }}
                mountOnEnter
                unmountOnExit={false}
              >
                <Nav variant="tabs" className="mb-3 flex-wrap flex-md-nowrap">
                  <Nav.Item className="w-50 w-md-auto">
                    <Nav.Link eventKey="prontuario" className="text-center">
                      Prontuário
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="w-50 w-md-auto">
                    <Nav.Link eventKey="receita" className="text-center">
                      Receita
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="w-50 w-md-auto">
                    <Nav.Link eventKey="atestado" className="text-center">
                      Atestado
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="w-50 w-md-auto">
                    <Nav.Link eventKey="requisicao" className="text-center">
                      Requisição
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="prontuario">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <CampoClinico
                        control={control}
                        name="QUEIXAS"
                        controlId="obs"
                        label="Queixa Principal e História"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          <ArraySelector
                            items={queixas}
                            label="Selecionar Queixa"
                            onSelect={(item) => appendSelectorItem('QUEIXAS', item)}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="MEDICAMENTOS"
                        controlId="editMedicacoes"
                        label="Medicações em Uso"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                      />

                      <CampoClinico
                        control={control}
                        name="HABITOS"
                        controlId="habitos"
                        label="Hábitos"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          <ArraySelector
                            items={habitos}
                            label="Selecionar Hábito"
                            onSelect={(item) => appendSelectorItem('HABITOS', item)}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="ANTECEDENTES_PESSOAIS"
                        controlId="exComplementares"
                        label="Antecedentes Pessoais/Familiares"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          <ArraySelector
                            items={antecedentes}
                            label="Selecionar Antecedente"
                            onSelect={(item) => appendSelectorItem('ANTECEDENTES_PESSOAIS', item)}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="EXAMES_COMPLEMENTARES"
                        controlId="editExame"
                        label="Exame Complementares Prévio"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                      />

                      <CampoClinico
                        control={control}
                        name="ANTECEDENTES_FLIA"
                        controlId="editExamesLaboratoriais"
                        label="Exames Laboratoriais (Histórico)"
                        labelClassName="mb-0"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        placeholder="Ex.: 15/05/24:G=93 CT=160 HDL=58 LDL=79 ..."
                        style={{
                          backgroundColor: blue[50],
                          border: '2px solid #011eff',
                          borderRadius: '8px',
                        }}
                        header={
                          <>
                            {/* PDF de exame laboratorial e análise por IA desativados — não usados hoje.
                      <div>
                        {(func || doctor) && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={isReadOnly || uploadingLabPdf || analyzingLabPdf}
                            onClick={handlePickLabPdf}
                          >
                            {uploadingLabPdf ? 'Anexando PDF...' : 'Anexar PDF'}
                          </Button>
                        )}{' '}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={isReadOnly || uploadingLabPdf || analyzingLabPdf}
                          onClick={handleProcessLabPdf}
                        >
                          {analyzingLabPdf ? 'Analisando PDF com IA...' : 'Analisar PDF com IA'}
                        </Button>{' '}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={viewingLabPdf}
                          onClick={handleViewLabPdf}
                        >
                          {viewingLabPdf ? 'Abrindo PDF...' : 'Ver PDF Laboratório'}
                        </Button>
                        <input
                          ref={labPdfInputRef}
                          type="file"
                          accept="application/pdf"
                          onChange={handleAttachLabPdf}
                          style={{ display: 'none' }}
                        />
                      </div>
                      */}
                          </>
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="EXAME_FISICO"
                        controlId="editISDA"
                        label="Exame Físico"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        style={{
                          backgroundColor: green[50],
                          border: '2px solid #388e3c',
                          borderRadius: '8px',
                        }}
                        header={
                          <ArraySelector
                            items={fisico}
                            label="Selecionar Exame Físico"
                            onSelect={(item) => {
                              if (item === 'Normal') {
                                setValue(
                                  'EXAME_FISICO',
                                  `PA=  mmHg   e  FC=  bpm e Peso= Kg\nRCR, 2T sem sopros\nMV +, sem ruídos adventícios\nAbdome flácido e indolor. Ausência de VMG, com RHA presentes\nAusência de edema em MMII\nPulsos presentes e simétricos\nCarótidas sem sopro`,
                                  { shouldDirty: true, shouldTouch: true }
                                );
                              } else {
                                appendSelectorItem('EXAME_FISICO', item);
                              }
                              handleGravarParcial();
                            }}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="ISDA"
                        controlId="editAntec"
                        label="ECG"
                        labelClassName="mb-0"
                        omitSize
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={loadingAtividades}
                              onClick={() => abrirAtividades(false)}
                            >
                              {loadingAtividades ? (
                                <>
                                  <Spinner animation="border" size="sm" className="mr-1" />{' '}
                                  Carregando...
                                </>
                              ) : (
                                '📋 Atividades'
                              )}
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={loadingAtividades}
                              onClick={() => abrirAtividades(true)}
                            >
                              {loadingAtividades ? (
                                <>
                                  <Spinner animation="border" size="sm" className="mr-1" />{' '}
                                  Carregando...
                                </>
                              ) : (
                                '❤️ ECG'
                              )}
                            </Button>
                          </div>
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="HIPOTESE_DIAGNOSTICA"
                        controlId="hipotese"
                        label="Hipótese Diagnóstica"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          <ArraySelector
                            items={hipotese}
                            label="Selecionar Hipótese Diagnóstica"
                            onSelect={(item) => appendSelectorItem('HIPOTESE_DIAGNOSTICA', item)}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="CONDUTA"
                        controlId="conduta"
                        label="Conduta"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        style={{
                          backgroundColor: green[50],
                          border: '2px solid #388e3c',
                          borderRadius: '8px',
                        }}
                        header={
                          <ArraySelector
                            items={conduta}
                            label="Selecionar Conduta"
                            onSelect={(item) => appendSelectorItem('CONDUTA', item)}
                            isDisabled={isFuncionario}
                          />
                        }
                      />

                      <CampoClinico
                        control={control}
                        name="OBS"
                        controlId="observacoes"
                        label="Observações"
                        labelClassName="mb-0"
                        isFuncionario={isFuncionario}
                        isReadOnly={isReadOnly}
                        onSave={handleGravarParcial}
                        header={
                          /* Anexo de documento (list-ia / upload-ia) desativado — não usado hoje.
                    <div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={isReadOnly || uploadingPatientDoc}
                        onClick={handlePickPatientDoc}
                      >
                        {uploadingPatientDoc ? 'Anexando documento...' : 'Anexar Documento'}
                      </Button>{' '}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={viewingPatientDoc}
                        onClick={handleViewPatientDoc}
                      >
                        Ver Anexo
                      </Button>
                      <input
                        ref={patientDocInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={handleAttachPatientDoc}
                        style={{ display: 'none' }}
                      />
                    </div>
                    */
                          null
                        }
                      />

                      <Row className="mb-3">
                        <CampoClinico
                          control={control}
                          name="CID"
                          controlId="cid1"
                          label="CID1"
                          textarea={false}
                          groupAs={Col}
                          isFuncionario={isFuncionario}
                          isReadOnly={isReadOnly}
                          onSave={handleGravarParcial}
                        />
                        <CampoClinico
                          control={control}
                          name="CID2"
                          controlId="cid2"
                          label="CID2"
                          textarea={false}
                          groupAs={Col}
                          isFuncionario={isFuncionario}
                          isReadOnly={isReadOnly}
                          onSave={handleGravarParcial}
                        />
                        <CampoClinico
                          control={control}
                          name="CID3"
                          controlId="cid3"
                          label="CID3"
                          textarea={false}
                          groupAs={Col}
                          isFuncionario={isFuncionario}
                          isReadOnly={isReadOnly}
                          onSave={handleGravarParcial}
                        />
                      </Row>
                    </Form>
                  </Tab.Pane>
                  <Tab.Pane eventKey="atestado">
                    <Atestado prontuario={prontuario} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="receita">
                    <FormReceitas embedded codPac={prontuario.CODPAC} consulta={prontuario} />
                  </Tab.Pane>
                  <Tab.Pane eventKey="requisicao">
                    <Requisicao prontuario={prontuario} />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          </div>
        )}
      </Card.Body>

      {/* Anexo de documento (list-ia / upload-ia) desativado — não usado hoje.
      <Modal show={showPatientDocModal} onHide={() => setShowPatientDocModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Anexos do Paciente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingPatientDocs ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : patientDocFiles.length === 0 ? (
            <div className="text-muted">Nenhum documento anexado para esta consulta.</div>
          ) : (
            <ListGroup>
              {patientDocFiles.map((file) => (
                <ListGroup.Item
                  key={file.key}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div style={{ maxWidth: '70%' }}>
                    <div className="fw-bold text-truncate">{file.filename}</div>
                    <small className="text-muted">
                      {file.createdAt && !Number.isNaN(file.createdAt.getTime())
                        ? file.createdAt.toLocaleString('pt-BR')
                        : 'Data não disponível'}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={viewingPatientDoc}
                      onClick={() => handleOpenPatientDocFromModal(file)}
                    >
                      {viewingPatientDoc ? 'Abrindo...' : 'Abrir'}
                    </Button>
                    {/* <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={viewingPatientDoc || deletingPatientDocKey === file.key}
                      onClick={() => handleDeletePatientDocFromModal(file)}
                    >
                      {deletingPatientDocKey === file.key ? 'Excluindo...' : 'Excluir'}
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>
      */}

      {/* PDF de exame laboratorial desativado — não usado hoje.
      <Modal show={showLabDocModal} onHide={() => setShowLabDocModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>PDFs Laboratoriais do Paciente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingLabDocs ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : labDocFiles.length === 0 ? (
            <div className="text-muted">Nenhum PDF laboratorial encontrado para este paciente.</div>
          ) : (
            <ListGroup>
              {labDocFiles.map((file) => (
                <ListGroup.Item
                  key={file.key}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div style={{ maxWidth: '70%' }}>
                    <div className="fw-bold text-truncate">{file.filename}</div>
                    <small className="text-muted">
                      {file.createdAt && !Number.isNaN(file.createdAt.getTime())
                        ? file.createdAt.toLocaleString('pt-BR')
                        : 'Data não disponível'}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={viewingLabPdf || deletingLabDocKey === file.key}
                      onClick={() => handleOpenLabDocFromModal(file)}
                    >
                      {viewingLabPdf ? 'Abrindo...' : 'Abrir'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={viewingLabPdf || deletingLabDocKey === file.key}
                      onClick={() => handleDeleteLabDocFromModal(file)}
                    >
                      {deletingLabDocKey === file.key ? 'Excluindo...' : 'Excluir'}
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>
      */}

      {!isLoading && !error && prontuario && abaAtiva === 'prontuario' && (
        <Card.Footer className="text-center">
          <div>
            {prontuario.HASH_INTEGRIDADE && (
              <>
                Autenticação Digital: <i>{prontuario.HASH_INTEGRIDADE}</i>
                <br />
              </>
            )}
            <strong>Assinado por: Dr. {prontuario.NOMEMED}</strong>
          </div>
          <div className="mt-2 d-flex flex-wrap justify-content-center gap-2">
            <Button
              className="w-100 w-md-auto"
              onClick={handleSubmit(onSubmit)}
              variant="success"
              disabled={isSubmitting || isFuncionario || prontuario.HASH_INTEGRIDADE || isReadOnly}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Gravar Parcial'}
            </Button>
            <Button
              className="w-100 w-md-auto"
              disabled={isSubmitting || isFuncionario || prontuario.HASH_INTEGRIDADE || isReadOnly}
              onClick={handleSubmit(async (data) => {
                const respostaBackend = await onSubmit({
                  ...data,
                  GRAVADO: 'SIM',
                  situacao: 'DEFINITIVO',
                });

                if (respostaBackend) {
                  const salvo = respostaBackend.data ?? {};
                  setProntuario((prev) => ({
                    ...prev,
                    HASH_INTEGRIDADE: salvo.hashIntegridade || prev?.HASH_INTEGRIDADE,
                    GRAVADO: 'SIM',
                    situacao: salvo.situacao || 'DEFINITIVO',
                  }));
                }
              })}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Gravar Definitivo'}
            </Button>
            <Button
              className="w-100 w-md-auto"
              variant="info"
              disabled={isFuncionario}
              onClick={() => handleImprimirProntuario({ prontuario })}
            >
              Imprimir
            </Button>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
}
